import React, {
  ReactElement,
  startTransition,
  useCallback,
  useRef,
  useState
} from "react";
import * as styles from "./HeroBannerSlideshow.module.scss";
import HeroBannerSlide from "./HeroBannerSlide";
import {
  HeroSlideContentFragment,
  HeroSlidePromoContentFragment
} from "types/graphql-types";
import { useGeolocation } from "../../utils/localization/geolocation/GeolocationContext";
import { BlockContent } from "components/BlockContent";

import { Swiper, SwiperClass, SwiperSlide } from "swiper/react";
import SwiperCore, { Autoplay, EffectFade } from "swiper";

import SliderControllers from "./SliderControllers";
import HeroBannerUrgentMessageSlide from "./HeroBannerUrgentMessageSlide";
import { isPresent } from "src/utils/checks";
import {
  BlockContentValue,
  isBlockContentValuePresent
} from "@silversea-ssc/ui-components-react";
import {
  filterActiveSlidesForGeolocation,
  sanityContentToHeroBannerSlideshowItem
} from "./utils";
import { useRemotePromos } from "src/ssc-api/useRemotePromos";

// eslint-disable-next-line react-hooks/rules-of-hooks
SwiperCore.use([Autoplay, EffectFade]);

interface HeroBannerSlideshowProps {
  slides: (HeroSlideContentFragment | HeroSlidePromoContentFragment)[];
  newsMessage?: BlockContentValue;
  enableAutoPlay: boolean;
  urgentMessageSlideContent?: BlockContentValue;
  promoBanner?: ReactElement;
}

const HeroBannerSlideshow: React.FC<
  React.PropsWithChildren<HeroBannerSlideshowProps>
> = ({
  slides: allSlides,
  newsMessage,
  enableAutoPlay,
  urgentMessageSlideContent,
  promoBanner
}) => {
  const geolocation = useGeolocation();
  const enablePhoneNumberSlide = isPresent(urgentMessageSlideContent);
  const { data: activePromos } = useRemotePromos();

  const slides = React.useMemo(
    () =>
      allSlides
        .filter(filterActiveSlidesForGeolocation(geolocation))
        .map(sanityContentToHeroBannerSlideshowItem(geolocation, activePromos))
        .filter(isPresent),
    [allSlides, geolocation, activePromos]
  );

  const sliderRef = useRef<HTMLDivElement | null>(null);
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);

  const prevSlide = useCallback(() => {
    swiper?.slidePrev();
  }, [swiper]);

  const nextSlide = useCallback(() => {
    swiper?.slideNext();
  }, [swiper]);

  const setSwiperInstance = useCallback((instance: SwiperClass) => {
    startTransition(() => {
      setSwiper(instance);
    });
  }, []);

  return (
    <section
      ref={sliderRef}
      className={styles.heroBanner}
      data-content-name="hero"
      aria-roledescription="carousel"
    >
      {promoBanner && <React.Fragment>{promoBanner}</React.Fragment>}
      {isBlockContentValuePresent(newsMessage) && (
        <div
          className={styles.newsMessageContainer}
          data-content-piece="news_message"
        >
          <div className={styles.newsMessage}>
            <BlockContent value={newsMessage} />
          </div>
        </div>
      )}

      <Swiper
        uniqueNavElements={false}
        loop={false}
        passiveListeners
        a11y={{ enabled: true }}
        autoplay={
          enableAutoPlay
            ? {
                delay: 8000,
                disableOnInteraction: false
              }
            : undefined
        }
        effect="fade"
        fadeEffect={{
          crossFade: true
        }}
        className={styles.swiperSlider}
        onSwiper={setSwiperInstance}
      >
        {enablePhoneNumberSlide && urgentMessageSlideContent && (
          <SwiperSlide>
            <HeroBannerUrgentMessageSlide
              content={urgentMessageSlideContent}
              sliderControls={
                <SliderControllers
                  enabled={slides.length > 1}
                  prevDisabled={true}
                  nextDisabled={false}
                  prevSlide={prevSlide}
                  nextSlide={nextSlide}
                  totalPages={slides.length + 1}
                  currentPage={0}
                />
              }
            />
          </SwiperSlide>
        )}

        {slides.map((slide, index) => {
          const realIndex = enablePhoneNumberSlide ? index + 1 : index;
          const realTotalPages = enablePhoneNumberSlide
            ? slides.length + 1
            : slides.length;
          return (
            <SwiperSlide key={slide._key}>
              <HeroBannerSlide
                loading={index <= 1 ? undefined : "lazy"}
                slide={slide}
                customLayoutName={
                  slide.customKey == "30-years" ? "30-years" : undefined
                }
                sliderControls={
                  <SliderControllers
                    enabled={realTotalPages > 1}
                    prevDisabled={realIndex == 0}
                    nextDisabled={realIndex == realTotalPages - 1}
                    prevSlide={prevSlide}
                    nextSlide={nextSlide}
                    totalPages={realTotalPages}
                    currentPage={realIndex}
                  />
                }
              />
            </SwiperSlide>
          );
        })}
      </Swiper>
    </section>
  );
};

export default React.memo(HeroBannerSlideshow);
